/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import {
  Children,
  type ForwardedRef,
  type HTMLAttributes,
  type ReactNode,
  createContext,
  forwardRef,
  isValidElement,
  useState,
} from 'react';
import { useFocusVisible, useFocusWithin, useHover } from 'react-aria';
import { type ContextValue, DEFAULT_SLOT, Provider, useContextProps } from 'react-aria-components';
import { AlertTriangleStrokeIcon } from '../../icons/alert-triangle-stroke/alert-triangle-stroke.icon';
import { Refresh1StrokeIcon } from '../../icons/refresh-1-stroke/refresh-1-stroke.icon';
import { $ } from '../../tokens.stylex';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { MentionContext } from '../mention/mention.component';
import { ProgressIndicatorContext } from '../progress-indicator/progress-indicator.component';
import { ReactionButtonContext } from '../reaction-button/reaction-button.component';
import { ReactionToggleButtonContext } from '../reaction-toggle-button/reaction-toggle-button.component';
import { ReplyPreviewContext } from '../reply-preview/reply-preview.component';
import { Text, TextContext } from '../text/text.component';
import { ViewContext } from '../view/view.component';

interface MessageProps extends Omit<HTMLAttributes<HTMLLIElement>, 'style' | 'className'> {
  variant?: keyof typeof bubbleVariantVariants;
  stackPosition?: keyof typeof stackPositionVariants['ghost'];
  /** Highlight all occurrences of this string within a plain-text message (case-insensitive). */
  query?: string;
  style?: stylex.StyleXStyles;
  children?: ReactNode;
}

const MessageContext = createContext<ContextValue<MessageProps, HTMLLIElement>>({});

const Message = forwardRef((props: MessageProps, ref: ForwardedRef<HTMLLIElement>) => {
  [props, ref] = useContextProps(props, ref, MessageContext);

  const { style, children, variant = 'ghost', stackPosition = 'solo', query, ...coreProps } = props;

  const isTextOnly = Children.toArray(children).every((c) => !isValidElement(c));

  const [isFocusWithin, setIsFocusWithin] = useState(false);

  const { hoverProps, isHovered } = useHover({});
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => setIsFocusWithin(true),
    onBlurWithin: () => setIsFocusWithin(false),
  });

  const { isFocusVisible } = useFocusVisible({ isTextInput: false });

  const actionsVisible = isHovered || (isFocusWithin && isFocusVisible);

  return (
    <li
      ref={ref}
      {...stylex.props(styles.base, variantGridVariants[variant], style)}
      {...hoverProps}
      {...focusWithinProps}
      {...coreProps}>
      <Provider
        values={[
          [
            ViewContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  style: [
                    bubbleStyles.base,
                    bubbleVariantVariants[variant],
                    stackPositionVariants[variant][stackPosition],
                    styles.contentArea,
                  ],
                },
                [MESSAGE_SLOTS.ACTIONS_VIEW]: {
                  style: [styles.actionsArea, !actionsVisible && styles.actionsAreaHidden],
                },
                [MESSAGE_SLOTS.STATE_VIEW]: {
                  style: styles.stateArea,
                },
              },
            },
          ],
          [
            ProgressIndicatorContext,
            {
              size: 12,
              style: progressIndicatorStyles.base
            },
          ],
          [
            TextContext,
            {
              size: 16,
              weight: 'regular',
              style: [
                bubbleVariantVariants[variant],
                variant === 'ghost' && bubbleStyles.ghost,
                variant === 'ghost' && textStyles.headingLike,
              ],
              color: variant === 'sent' ? 'inverted' : 'primary',
            },
          ],
          [
            ReactionToggleButtonContext,
            {
              style: styles.reactionsArea,
            },
          ],
          [
            ReactionButtonContext,
            {
              style: styles.reactionsArea,
            },
          ],
          [
            IconButtonContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 24,
                  variant: 'ghost',
                  isRounded: true,
                },
                [MESSAGE_SLOTS.RETRY]: {
                  size: 20,
                  variant: 'ghost',
                  isRounded: true,
                  children: (renderProps) => renderProps.isHovered ? <Refresh1StrokeIcon /> : <AlertTriangleStrokeIcon color="critical" />
                },
              },
            },
          ],
          [
            MentionContext,
            {
              isOwn: variant === 'sent',
            },
          ],
          [
            ReplyPreviewContext,
            {
              variant,
            },
          ],
        ]}>
        {isTextOnly ? (
          <Text
            query={query}
            highlightColor={variant === 'sent' ? 'yellow' : 'yellowLight'}
            style={[
              bubbleStyles.base,
              bubbleVariantVariants[variant],
              variant === 'ghost' && bubbleStyles.ghost,
              variant === 'ghost' && textStyles.headingLike,
              stackPositionVariants[variant][stackPosition],
              styles.contentArea,
            ]}>
            {children}
          </Text>
        ) : (
          children
        )}
      </Provider>
    </li>
  );
});

const MESSAGE_SLOTS = {
  ACTIONS_VIEW: 'actions-view',
  RETRY: 'retry',
  STATE_VIEW: 'state-view',
} as const;

const styles = stylex.create({
  actionsArea: {
    gridArea: 'actions',
    gap: 2,
    transition: 'visibility 0s, opacity 0.15s ease',
    alignItems: 'center',
    alignSelf: 'center',
    display: 'flex',
    opacity: 1,
    visibility: 'visible',
  },
  actionsAreaHidden: {
    opacity: 0,
    visibility: 'hidden',
  },
  base: {
    listStyle: 'none',
    display: 'grid',
  },
  contentArea: {
    gridArea: 'content',
  },
  reactionsArea: {
    gridArea: 'reactions',
    alignSelf: 'start',
    justifySelf: 'start',
    marginLeft: 7,
    marginTop: -8,
  },
  stateArea: {
    gridArea: 'actions',
    alignItems: 'center',
    alignSelf: 'center',
    display: 'flex',
    justifyContent: 'end',
  },
});

const bubbleStyles = stylex.create({
  base: {
    borderRadius: 12,
    gap: 8,
    paddingBlock: 8,
    paddingInline: 10,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
  },
  ghost: {
    paddingInline: 4,
    paddingBottom: 0,
    paddingTop: 4,
  },
});

const textStyles = stylex.create({
  headingLike: {
    fontSize: 40,
    letterSpacing: -0.8,
    lineHeight: '48px',
  },
});

const progressIndicatorStyles = stylex.create({
  base: {
    margin: 6,
  }
});

const stackPositionVariants = {
  received: stylex.create({
    solo: { borderBottomLeftRadius: 12, borderTopLeftRadius: 12 },
    top: { borderBottomLeftRadius: 4, borderTopLeftRadius: 12 },
    middle: { borderBottomLeftRadius: 4, borderTopLeftRadius: 4 },
    bottom: { borderBottomLeftRadius: 12, borderTopLeftRadius: 4 },
  }),
  sent: stylex.create({
    solo: { borderBottomRightRadius: 12, borderTopRightRadius: 12 },
    top: { borderBottomRightRadius: 4, borderTopRightRadius: 12 },
    middle: { borderBottomRightRadius: 4, borderTopRightRadius: 4 },
    bottom: { borderBottomRightRadius: 12, borderTopRightRadius: 4 },
  }),
  ghost: stylex.create({
    solo: { borderBottomLeftRadius: 12, borderTopLeftRadius: 12 },
    top: { borderBottomLeftRadius: 4, borderTopLeftRadius: 12 },
    middle: { borderBottomLeftRadius: 4, borderTopLeftRadius: 4 },
    bottom: { borderBottomLeftRadius: 12, borderTopLeftRadius: 4 },
  }),
};

const bubbleVariantVariants = stylex.create({
  ghost: {
    backgroundColor: null,
  },
  received: {
    backgroundColor: $.surfaceBGSecondary,
  },
  sent: {
    backgroundColor: $.surfaceHighlight,
  },
});

const variantGridVariants = stylex.create({
  ghost: {
    gridTemplateAreas: '"content actions" "reactions reactions"',
    columnGap: 2,
    gridTemplateColumns: 'auto 1fr',
  },
  received: {
    gridTemplateAreas: '"content actions" "reactions reactions"',
    columnGap: 2,
    gridTemplateColumns: 'auto 1fr',
  },
  sent: {
    gridTemplateAreas: '"actions content" ". reactions"',
    columnGap: 2,
    gridTemplateColumns: '1fr auto',
  },
});

export { MESSAGE_SLOTS, Message, MessageContext, type MessageProps };
